import { useEffect, useMemo, useState } from 'react'
import { calculerFacture, tauxTVA } from '../../api/compta'
import { annulerReglementFacture, apercuFacturesMois, chargerLignesFacture, creerFacture, genererFacturesMois, reglerFacture } from '../../api/operations'
import {
  coursesLaisseesDeCote,
  LIBELLE_MOTIF,
  libelleMois,
  moisDepuisChamp,
  moisFacturable,
  moisPrecedent,
  resumeGeneration,
  type RapportFacturation,
} from '../../api/facturation'
import { estMobileMoney, fraisMobileMoney, tauxEncaissement } from '../../api/encaissement'
import { controlerAnnulationReglement, controlerReglement, estReglee, libelleMoyen, MESSAGE_MIGRATION_0023, MOYENS_REGLEMENT } from '../../api/reglement'
import type { Facture, LigneFacture, MoyenReglement } from '../../api/types'
import { useDroits } from '../Contexte'
import { dateCourte, fcfa } from '../Format'
import { Icone } from '../Icone'
import { ListeDeroulante } from '../ListeDeroulante'
import { Modale } from '../Modale'
import { Carte, exporterCSV, Table, type PropsCompta } from './briques'

const STATUT: Record<Facture['status'], [string, string]> = {
  issued: ['Émise', 'b-gold'],
  paid: ['Réglée', 'b-green'],
  cancelled: ['Annulée', 'b-red'],
}

/** Statut affiché : « payée » SANS date de règlement n'éteint pas la créance, on le dit. */
function statutAffiche(f: Facture): [string, string] {
  if (f.status === 'paid' && !f.paid_at) return ['Payée, sans date', 'b-warn']
  return STATUT[f.status]
}

const aujourdHui = () => new Date().toISOString().slice(0, 10)

/** Factures — transposition de l'onglet Factures, de `openInvoiceModal` et de `viewInvoice`. */
export function Factures(p: PropsCompta) {
  const { donnees, surNotifier } = p
  const droits = useDroits()
  const [nouvelle, setNouvelle] = useState(false)
  const [vue, setVue] = useState<Facture | null>(null)
  const [aRegler, setARegler] = useState<Facture | null>(null)
  const [aAnnuler, setAAnnuler] = useState<Facture | null>(null)
  const [mensuelle, setMensuelle] = useState(false)
  const o = donnees.org
  const peutRegler = droits.peut('factures')
  const reglementsDispo = donnees.synchro.reglements
  return (
    <>
      <Carte
        titre="Factures"
        icone="receipt"
        sous={`${donnees.invoices.length} facture${donnees.invoices.length > 1 ? 's' : ''} · numérotation séquentielle serveur · cliquez pour voir et imprimer`}
        actions={
          <>
            <span className="tva-tag">{o.vat_registered ? `Assujetti TVA ${o.vat_rate} %` : 'Non assujetti TVA'}</span>
            {droits.peut('factures') && (
              <>
                <button className="btn btn-gold btn-sm" style={{ marginLeft: 8 }} onClick={() => setMensuelle(true)}>
                  <Icone nom="receipt" /> Générer les factures du mois
                </button>
                <button className="btn btn-outline btn-sm" style={{ marginLeft: 8 }} onClick={() => setNouvelle(true)}>
                  <Icone nom="plus" /> Nouvelle facture
                </button>
              </>
            )}
          </>
        }
        surExport={() =>
          exporterCSV(
            'factures.csv',
            [['Numéro', 'Date', 'Client', 'HT', 'TVA', 'TTC', 'Statut', 'Date de règlement', 'Moyen'], ...donnees.invoices.map((f) => [f.number, dateCourte(f.issue_date), f.client_name, f.subtotal_ht_fcfa, f.vat_fcfa, f.total_ttc_fcfa, statutAffiche(f)[0], dateCourte(f.paid_at), libelleMoyen(f.payment_method)])],
            surNotifier,
          )
        }
      >
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>Numéro</th>
                <th>Date</th>
                <th>Client</th>
                <th style={{ textAlign: 'right' }}>HT</th>
                <th style={{ textAlign: 'right' }}>TVA</th>
                <th style={{ textAlign: 'right' }}>TTC</th>
                <th>Statut</th>
                <th>Règlement</th>
              </tr>
            </thead>
            <tbody>
              {donnees.invoices.length === 0 && (
                <tr>
                  <td colSpan={8} className="t-muted">
                    Aucune facture — « Générer les factures du mois » facture les PME au forfait ; « Nouvelle facture » facture les livraisons d’un client à l’unité.
                  </td>
                </tr>
              )}
              {donnees.invoices.map((f) => (
                <tr
                  key={f.id}
                  style={{ cursor: 'pointer' }}
                  tabIndex={0}
                  onClick={() => setVue(f)}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setVue(f)}
                >
                  <td className="mono">
                    {f.number}
                    {f.periode_mois && <div className="t-muted" style={{ fontSize: 11, fontFamily: 'inherit' }}>mensuelle · {libelleMois(f.periode_mois)}</div>}
                  </td>
                  <td className="t-muted">{dateCourte(f.issue_date)}</td>
                  <td className="t-strong">{f.client_name}</td>
                  <td className="mono" style={{ textAlign: 'right' }}>{fcfa(f.subtotal_ht_fcfa)}</td>
                  <td className="mono" style={{ textAlign: 'right' }}>{fcfa(f.vat_fcfa)}</td>
                  <td className="mono" style={{ textAlign: 'right' }}>{fcfa(f.total_ttc_fcfa)}</td>
                  <td>
                    <span className={`badge ${statutAffiche(f)[1]} plain`}>{statutAffiche(f)[0]}</span>
                  </td>
                  <td onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                    {estReglee(f) ? (
                      <>
                        <span className="t-muted">
                          {dateCourte(f.paid_at)}
                          {f.payment_method ? ` · ${libelleMoyen(f.payment_method)}` : ''}
                        </span>
                        {peutRegler && (
                          <button className="btn btn-outline btn-sm" style={{ marginLeft: 8 }} onClick={() => setAAnnuler(f)}>
                            Annuler le règlement
                          </button>
                        )}
                      </>
                    ) : (
                      f.status !== 'cancelled' &&
                      peutRegler && (
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => (reglementsDispo ? setARegler(f) : surNotifier(MESSAGE_MIGRATION_0023))}
                          title={reglementsDispo ? undefined : MESSAGE_MIGRATION_0023}
                          aria-disabled={!reglementsDispo}
                          style={reglementsDispo ? undefined : { opacity: 0.55 }}
                        >
                          <Icone nom="check" /> Marquer réglée
                        </button>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Carte>
      {nouvelle && <ModaleNouvelleFacture {...p} surFermer={() => setNouvelle(false)} />}
      {mensuelle && <ModaleGenerationMensuelle {...p} surFermer={() => setMensuelle(false)} />}
      {aRegler && <ModaleReglement facture={aRegler} p={p} surFermer={() => setARegler(null)} />}
      {aAnnuler && <ModaleAnnulationReglement facture={aAnnuler} p={p} surFermer={() => setAAnnuler(null)} />}
      {vue && <VueFacture facture={vue} p={p} surFermer={() => setVue(null)} />}
    </>
  )
}

/** Règlement d'une facture : date (pas dans le futur), moyen, confirmation. */
function ModaleReglement({ facture: f, p, surFermer }: { facture: Facture; p: PropsCompta; surFermer: () => void }) {
  const { surNotifier, surRafraichir, verrou } = p
  const [date, setDate] = useState(aujourdHui())
  const [moyen, setMoyen] = useState<MoyenReglement>('especes')
  const [enCours, setEnCours] = useState(false)
  const [erreur, setErreur] = useState('')

  async function enregistrer() {
    const motifs = controlerReglement({ date, moyen, issue_date: f.issue_date }, aujourdHui(), verrou)
    if (motifs.length) return setErreur(motifs.join(' '))
    setErreur('')
    setEnCours(true)
    try {
      const r = await reglerFacture(f.id, { date, moyen })
      surNotifier(`Facture ${f.number} réglée le ${dateCourte(date)} (${libelleMoyen(moyen)})${r.fraisFcfa > 0 ? ` — frais mobile money ${fcfa(r.fraisFcfa)}` : ''}`)
      surFermer()
      await surRafraichir()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e))
    } finally {
      setEnCours(false)
    }
  }

  return (
    <Modale titre={`Marquer réglée — ${f.number}`} ouverte surFermer={surFermer} surEnregistrer={() => void enregistrer()} libelleEnregistrer="Confirmer le règlement" enCours={enCours}>
      <p className="t-muted" style={{ fontSize: 12.5, margin: '0 0 10px' }}>
        {f.client_name} · {fcfa(f.total_ttc_fcfa)} TTC · émise le {dateCourte(f.issue_date)}
      </p>
      <div className="field">
        <label htmlFor="rg-date">Date de règlement</label>
        <input id="rg-date" type="date" value={date} max={aujourdHui()} min={f.issue_date.slice(0, 10)} onChange={(e) => setDate(e.target.value)} />
      </div>
      <div className="field">
        <label>Moyen de règlement</label>
        <ListeDeroulante
          valeur={moyen}
          options={MOYENS_REGLEMENT.map((m) => ({ valeur: m.valeur, libelle: m.libelle }))}
          surChangement={(v) => setMoyen(v as MoyenReglement)}
          pleineLargeur
        />
      </div>
      <p className="t-muted" style={{ fontSize: 12 }}>
        L’écriture de règlement sera datée du {dateCourte(date)} et passera par {moyen === 'especes' ? 'la caisse (571)' : moyen === 'wave' || moyen === 'orange_money' ? 'le compte de monnaie électronique (551)' : 'la banque (521)'}.
        {estMobileMoney(moyen) &&
          (() => {
            const taux = tauxEncaissement(p.donnees.tarifs, moyen === 'wave' ? 'wave' : 'orange_money')
            return taux > 0
              ? ` Frais ${libelleMoyen(moyen)} (${(taux * 100).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} %) : environ ${fcfa(fraisMobileMoney(f.total_ttc_fcfa, taux))}, comptabilisés en charge (631) — montant définitif calculé par la base.`
              : ' Aucun taux de frais n’est renseigné pour ce moyen (Réglages) : aucun frais ne sera comptabilisé.'
          })()}
      </p>
      {erreur && (
        <p role="alert" style={{ color: 'var(--err)', fontSize: 12.5, fontWeight: 600 }}>
          {erreur}
        </p>
      )}
    </Modale>
  )
}

/** Annulation d'un règlement saisi par erreur — refusée si sa date est dans une période clôturée. */
function ModaleAnnulationReglement({ facture: f, p, surFermer }: { facture: Facture; p: PropsCompta; surFermer: () => void }) {
  const { surNotifier, surRafraichir, verrou } = p
  const [enCours, setEnCours] = useState(false)
  const [erreur, setErreur] = useState('')

  async function annuler() {
    const motifs = controlerAnnulationReglement(f.paid_at, verrou)
    if (motifs.length) return setErreur(motifs.join(' '))
    setErreur('')
    setEnCours(true)
    try {
      await annulerReglementFacture(f.id)
      surNotifier(`Règlement de la facture ${f.number} annulé`)
      surFermer()
      await surRafraichir()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e))
    } finally {
      setEnCours(false)
    }
  }

  return (
    <Modale titre={`Annuler le règlement — ${f.number}`} ouverte surFermer={surFermer} surEnregistrer={() => void annuler()} libelleEnregistrer="Annuler le règlement" enCours={enCours}>
      <p style={{ fontSize: 13.5 }}>
        La facture {f.number} ({f.client_name}, {fcfa(f.total_ttc_fcfa)}) réglée le {dateCourte(f.paid_at)}
        {f.payment_method ? ` par ${libelleMoyen(f.payment_method)}` : ''} redeviendra <b>émise, non réglée</b> : sa créance sera de nouveau due et l’écriture de règlement disparaîtra des livres.
      </p>
      {erreur && (
        <p role="alert" style={{ color: 'var(--err)', fontSize: 12.5, fontWeight: 600 }}>
          {erreur}
        </p>
      )}
    </Modale>
  )
}

/**
 * « Générer les factures du mois » : UNE facture par client PME abonné, montants calculés PAR LA BASE.
 * L'aperçu (nombre, montants, clients sautés et pourquoi) vient de la même fonction que la génération — il ne
 * peut pas diverger. Confirmer émet les factures (datées d'aujourd'hui) ; relancer ne duplique rien.
 */
function ModaleGenerationMensuelle({ orgId, surNotifier, surRafraichir, surFermer }: PropsCompta & { surFermer: () => void }) {
  const [mois, setMois] = useState(moisPrecedent())
  const [forcer, setForcer] = useState(false)
  const [apercu, setApercu] = useState<RapportFacturation | null>(null)
  const [chargement, setChargement] = useState(false)
  const [erreur, setErreur] = useState('')
  const [enCours, setEnCours] = useState(false)
  const facturable = moisFacturable(mois)

  useEffect(() => {
    setApercu(null)
    setErreur('')
    if (!facturable) return
    let vivant = true
    setChargement(true)
    apercuFacturesMois(orgId, moisDepuisChamp(mois), forcer)
      .then((r) => vivant && setApercu(r))
      .catch((e: unknown) => vivant && setErreur(e instanceof Error ? e.message : String(e)))
      .finally(() => vivant && setChargement(false))
    return () => {
      vivant = false
    }
  }, [orgId, mois, forcer, facturable])

  const n = apercu?.factures.length ?? 0
  const ht = apercu?.factures.reduce((s, f) => s + f.total_ht, 0) ?? 0
  const tva = apercu?.factures.reduce((s, f) => s + f.tva, 0) ?? 0
  const delaisse = apercu ? coursesLaisseesDeCote(apercu) : 0
  const pasEcheance = apercu?.ignores.filter((i) => i.motif === 'pas_a_echeance').length ?? 0

  async function generer() {
    if (!apercu || n === 0) return
    setEnCours(true)
    setErreur('')
    try {
      const r = await genererFacturesMois(orgId, moisDepuisChamp(mois), forcer)
      surNotifier(`${resumeGeneration(r)} — ${fcfa(r.total_ttc)} TTC`)
      surFermer()
      await surRafraichir()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e))
    } finally {
      setEnCours(false)
    }
  }

  return (
    <Modale titre="Générer les factures du mois" ouverte surFermer={surFermer} surEnregistrer={() => void generer()} libelleEnregistrer={n > 0 ? `Générer ${n} facture${n > 1 ? 's' : ''} (${fcfa(apercu?.total_ttc)} TTC)` : 'Générer'} enCours={enCours} desactive={chargement || n === 0} large>
      <div className="mois-resume">
        <div className="barre-mois">
          <div className="field">
            <label htmlFor="gm-mois">Mois à facturer</label>
            <input id="gm-mois" type="month" value={mois} onChange={(e) => setMois(e.target.value)} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer', paddingBottom: 10 }}>
            <input type="checkbox" style={{ width: 'auto' }} checked={forcer} onChange={(e) => setForcer(e.target.checked)} />
            Inclure les clients dont le jour de facturation n’est pas atteint
          </label>
        </div>

        {!facturable && (
          <div className="bandeau alerte">
            {mois ? `Le mois de ${libelleMois(mois)} n’est pas terminé : on ne facture qu’un mois dont le dernier jour est passé.` : 'Choisissez un mois.'}
          </div>
        )}
        {chargement && <div className="t-muted">Calcul de l’aperçu…</div>}
        {erreur && (
          <p role="alert" style={{ color: 'var(--err)', fontSize: 12.5, fontWeight: 600 }}>
            {erreur}
          </p>
        )}

        {apercu && (
          <>
            <div className="encart">
              <b>
                {n} facture{n > 1 ? 's' : ''} à générer pour {apercu.libelle_mois || libelleMois(apercu.mois)}
              </b>{' '}
              — {fcfa(apercu.total_ttc)} TTC (HT {fcfa(ht)} · TVA {fcfa(tva)}). Émises, datées d’aujourd’hui ; relancer ne crée aucun doublon.
            </div>
            {n > 0 && (
              <Table
                entetes={['Client', 'Forfait', 'Courses', 'Dépassements', 'Suppl. + longue dist. + forte demande', 'Total TTC']}
                droite={[3, 4, 5]}
                vide=""
                lignes={apercu.factures.map((f) => [
                  f.client,
                  `${f.forfait} · ${fcfa(f.forfait_ttc)}${f.option_ttc > 0 ? ` + option ${fcfa(f.option_ttc)}` : ''}`,
                  `${f.courses}${f.quota !== null ? ` / ${f.quota}` : ''}`,
                  f.depassements > 0 ? `${f.depassements} · ${fcfa(f.depassement_ttc)}` : '—',
                  f.supplement_ttc + f.longue_distance_ttc + f.majoration_ttc > 0 ? fcfa(f.supplement_ttc + f.longue_distance_ttc + f.majoration_ttc) : '—',
                  fcfa(f.total_ttc),
                ])}
                pied={['Total', '', '', '', '', fcfa(apercu.total_ttc)]}
              />
            )}
            {apercu.ignores.length > 0 && (
              <>
                <h4 style={{ margin: '4px 0 0' }}>Clients sautés ({apercu.ignores.length})</h4>
                <Table
                  entetes={['Client', 'Pourquoi', 'Courses non facturées']}
                  droite={[2]}
                  vide=""
                  lignes={apercu.ignores.map((i) => [
                    i.client,
                    <span key={i.client_id}>
                      {LIBELLE_MOTIF[i.motif]}
                      {i.detail && <span className="t-muted"> — {i.motif === 'pas_a_echeance' ? `à partir du ${dateCourte(i.detail)}` : i.detail}</span>}
                    </span>,
                    i.courses_non_facturees > 0 ? String(i.courses_non_facturees) : '—',
                  ])}
                />
              </>
            )}
            {delaisse > 0 && (
              <div className="bandeau alerte">
                {delaisse} course{delaisse > 1 ? 's livrées restent' : ' livrée reste'} non facturée{delaisse > 1 ? 's' : ''} (clients sans abonnement ce mois-là) : souscrivez-les à un forfait, ou facturez-les à l’unité (« Nouvelle facture »).
              </div>
            )}
            {pasEcheance > 0 && !forcer && (
              <div className="t-muted" style={{ fontSize: 12 }}>
                {pasEcheance} client{pasEcheance > 1 ? 's ne sont' : ' n’est'} pas encore à échéance (jour de facturation de leur abonnement) : revenez après ce jour, ou cochez « Inclure… ».
              </div>
            )}
            {apercu.ignores.some((i) => i.motif === 'erreur') && (
              <div className="bandeau erreur">
                Au moins un client est en erreur ; sa facture ne sera pas écrite. Motif : {apercu.ignores.find((i) => i.motif === 'erreur')?.detail}
              </div>
            )}
          </>
        )}
      </div>
    </Modale>
  )
}

function ModaleNouvelleFacture({ donnees, orgId, surNotifier, surRafraichir, surFermer }: PropsCompta & { surFermer: () => void }) {
  const [clientId, setClientId] = useState(donnees.clients.find((c) => c.type === 'pme')?.id ?? donnees.clients[0]?.id ?? '')
  const [coches, setCoches] = useState<Set<string>>(new Set())
  const [libre, setLibre] = useState({ description: 'Prestation de livraison', ttc: 0 })
  const [note, setNote] = useState('')
  const [enCours, setEnCours] = useState(false)
  const taux = tauxTVA(donnees.org)

  /* Les courses déjà portées par une facture non annulée sont écartées : elles
     revenaient pré-cochées dans l'original, et le client payait deux fois.
     Source : TOUTES les livraisons (`livrees`), pas la fenêtre de 24 h. */
  /* Une course qui porte un paiement ENREGISTRÉ n'est plus facturable non plus : la créance est éteinte par le
     paiement, la facturer la ferait régler deux fois (la base le refuse : 0033). */
  const encaissees = useMemo(() => new Set(donnees.encaissements.filter((e) => e.status === 'succeeded').map((e) => e.course_id)), [donnees.encaissements])
  const aFacturer = useMemo(
    () => donnees.livrees.filter((c) => c.client_account_id === clientId && !donnees.billed.has(c.id) && !encaissees.has(c.id)),
    [donnees.livrees, donnees.billed, encaissees, clientId],
  )
  const dejaFacturees = donnees.livrees.filter((c) => c.client_account_id === clientId && (donnees.billed.has(c.id) || encaissees.has(c.id))).length

  useEffect(() => setCoches(new Set(aFacturer.map((c) => c.id))), [aFacturer])

  const choisies = aFacturer.filter((c) => coches.has(c.id))
  const montants = aFacturer.length ? choisies.map((c) => c.price_fcfa) : libre.ttc > 0 ? [libre.ttc] : []
  const t = calculerFacture(montants, taux)

  async function enregistrer() {
    const client = donnees.clients.find((c) => c.id === clientId)
    if (!client) return surNotifier('Choisissez un client')
    if (montants.length === 0) return surNotifier('Aucune ligne à facturer')
    const lignes: LigneFacture[] = aFacturer.length
      ? choisies.map((c, i) => ({
          course_id: c.id,
          description: `Livraison ${c.ref} (${c.pickup} → ${c.dropoff})`,
          qty: 1,
          unit_price_ht_fcfa: t.lignesHT[i] ?? 0,
          amount_ht_fcfa: t.lignesHT[i] ?? 0,
        }))
      : [{ course_id: null, description: libre.description.trim() || 'Prestation', qty: 1, unit_price_ht_fcfa: t.ht, amount_ht_fcfa: t.ht }]
    setEnCours(true)
    try {
      const f = await creerFacture(orgId, {
        client_account_id: client.id,
        client_name: client.name,
        issue_date: new Date().toISOString().slice(0, 10),
        subtotal_ht_fcfa: t.ht,
        /* Taux NUL si l'organisation n'est pas assujettie : l'original écrivait
           18 % avec une TVA à zéro, et la facture imprimée affichait « TVA (18 %) 0 F ». */
        vat_rate: donnees.org.vat_registered ? donnees.org.vat_rate : 0,
        vat_fcfa: t.tva,
        total_ttc_fcfa: t.ttc,
        notes: note.trim() || null,
        lines: lignes,
      })
      surNotifier(`Facture ${f.number} créée`)
      surFermer()
      await surRafraichir()
    } catch (e) {
      surNotifier(`Erreur : ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setEnCours(false)
    }
  }

  return (
    <Modale titre="Nouvelle facture" ouverte surFermer={surFermer} surEnregistrer={() => void enregistrer()} libelleEnregistrer="Émettre la facture" enCours={enCours}>
      <div className="field">
        <label>Client</label>
        <ListeDeroulante
          valeur={clientId}
          options={donnees.clients.map((c) => ({ valeur: c.id, libelle: `${c.name}${c.type === 'pme' ? ' (PME)' : ''}` }))}
          surChangement={setClientId}
          pleineLargeur
        />
      </div>
      {dejaFacturees > 0 && (
        <p className="t-muted" style={{ fontSize: 12, margin: '0 0 8px' }}>
          {dejaFacturees} livraison{dejaFacturees > 1 ? 's' : ''} de ce client {dejaFacturees > 1 ? 'sont' : 'est'} déjà facturée
          {dejaFacturees > 1 ? 's' : ''} ou encaissée{dejaFacturees > 1 ? 's' : ''} — écartée{dejaFacturees > 1 ? 's' : ''} de cette liste.
        </p>
      )}
      {aFacturer.length > 0 ? (
        <div className="field">
          <label>Livraisons à facturer (montants TTC annoncés)</label>
          {aFacturer.map((c) => (
            <button
              type="button"
              key={c.id}
              className={`perm-toggle${coches.has(c.id) ? ' on' : ''}`}
              style={{ marginTop: 6, width: '100%', background: undefined }}
              aria-pressed={coches.has(c.id)}
              onClick={() =>
                setCoches((s) => {
                  const n = new Set(s)
                  if (n.has(c.id)) n.delete(c.id)
                  else n.add(c.id)
                  return n
                })
              }
            >
              <span className="box">
                <Icone nom="check" />
              </span>
              <span style={{ flex: 1, textAlign: 'left' }}>
                {c.ref} · {dateCourte(c.delivered_at)} · {c.pickup} → {c.dropoff}
              </span>
              <b>{fcfa(c.price_fcfa)}</b>
            </button>
          ))}
        </div>
      ) : (
        <>
          <div className="field">
            <label htmlFor="fa-desc">Prestation</label>
            <input id="fa-desc" value={libre.description} onChange={(e) => setLibre({ ...libre, description: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="fa-ttc">Montant facturé TTC (F) — la TVA en est extraite</label>
            <input
              id="fa-ttc"
              type="number"
              min={0}
              step={500}
              value={libre.ttc}
              onChange={(e) => setLibre({ ...libre, ttc: Math.max(0, parseInt(e.target.value, 10) || 0) })}
            />
          </div>
        </>
      )}
      <div className="field">
        <label htmlFor="fa-note">Note (facultatif)</label>
        <input id="fa-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="ex. Règlement mobile money à réception" />
      </div>
      <p className="t-muted" style={{ fontSize: 12.5 }}>
        {montants.length ? (
          <>
            Total HT {fcfa(t.ht)} · TVA {fcfa(t.tva)} · <b style={{ color: 'var(--ink)' }}>TTC {fcfa(t.ttc)}</b>
          </>
        ) : (
          'Sélectionnez au moins une ligne.'
        )}
      </p>
    </Modale>
  )
}

function VueFacture({ facture: f, p, surFermer }: { facture: Facture; p: PropsCompta; surFermer: () => void }) {
  const o = p.donnees.org
  const client = p.donnees.clients.find((c) => c.id === f.client_account_id)
  const [lignes, setLignes] = useState<LigneFacture[] | null>(null)
  const { apercu } = useDroits()
  useEffect(() => {
    if (apercu) return setLignes([])
    chargerLignesFacture(f.id)
      .then(setLignes)
      .catch(() => setLignes([]))
  }, [f.id, apercu])
  const affichees: LigneFacture[] =
    lignes && lignes.length
      ? lignes
      : [{ course_id: null, description: 'Prestation de livraison', qty: 1, unit_price_ht_fcfa: f.subtotal_ht_fcfa, amount_ht_fcfa: f.subtotal_ht_fcfa }]
  return (
    <Modale titre={`Facture ${f.number}`} ouverte surFermer={surFermer} surEnregistrer={() => window.print()} libelleEnregistrer="Imprimer">
      <div className="invoice-doc" id="invoice-print">
        <div className="inv-top">
          <div className="inv-org">
            <div className="io-name">{o.name}</div>
            <div className="io-line">
              {o.legal_form ?? ''}
              {o.address ? ` · ${o.address}` : ''}
            </div>
            <div className="io-line">
              NIF {o.nif || '—'} · RCCM {o.rccm || '—'}
            </div>
            <div className="io-line">
              {o.phone ?? ''}
              {o.email ? ` · ${o.email}` : ''}
            </div>
          </div>
          <div className="inv-meta">
            <div className="im-title">FACTURE</div>
            <div className="im-num">{f.number}</div>
            <div className="io-line">Date : {dateCourte(f.issue_date)}</div>
            {f.paid_at && (
              <div className="io-line">
                Réglée le {dateCourte(f.paid_at)}
                {f.payment_method ? ` · ${libelleMoyen(f.payment_method)}` : ''}
              </div>
            )}
          </div>
        </div>
        <div className="inv-parties">
          <div>
            <div className="ip-l">Émetteur</div>
            {o.name}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="ip-l">Facturé à</div>
            {f.client_name}
            {client?.phone && (
              <>
                <br />
                {client.phone}
              </>
            )}
          </div>
        </div>
        {lignes === null ? (
          <p className="t-muted">Chargement des lignes…</p>
        ) : (
          <Table
            entetes={['Désignation', 'Qté', 'P.U. HT', 'Montant HT']}
            droite={[2, 3]}
            vide=""
            lignes={affichees.map((l) => [l.description, String(l.qty || 1), fcfa(l.unit_price_ht_fcfa || l.amount_ht_fcfa), fcfa(l.amount_ht_fcfa)])}
          />
        )}
        <div className="inv-tot">
          <div className="tr">
            <span>Total HT</span>
            <span>{fcfa(f.subtotal_ht_fcfa)}</span>
          </div>
          <div className="tr">
            <span>TVA ({f.vat_rate} %)</span>
            <span>{fcfa(f.vat_fcfa)}</span>
          </div>
          <div className="tr grand">
            <span>Total TTC</span>
            <span>{fcfa(f.total_ttc_fcfa)}</span>
          </div>
        </div>
        <div className="inv-legal">
          Facture établie conformément au SYSCOHADA révisé. Montants en {o.currency || 'FCFA'}.{' '}
          {f.vat_fcfa > 0 ? `TVA au taux de ${f.vat_rate} %.` : 'TVA non applicable — régime non assujetti.'}
          {f.status === 'cancelled' && <b> FACTURE ANNULÉE.</b>}
          {f.notes && (
            <>
              <br />
              {f.notes}
            </>
          )}
        </div>
      </div>
    </Modale>
  )
}
